import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Building2, UserPlus, Key } from "lucide-react";
import { toast } from "sonner";
import { formatUSPhone } from "../lib/phone";
import USDateInput from "./USDateInput";

/**
 * "Add tenant to property" — onboarding shortcut that creates a tenant AND
 * their lease in one flow. Perfect for a landlord who just added a property
 * and needs to load its existing tenants without jumping between pages.
 *
 * Backend calls:
 *   1. POST /tenants          — create the tenant
 *   2. POST /leases           — link tenant to property + unit + rent + dates
 *
 * Props:
 *   - open        boolean
 *   - onOpenChange(bool)
 *   - onCreated() callback fired after successful tenant+lease creation
 *                 so the parent can refresh its tenant list
 *   - isAdmin     boolean — shows the investor picker for superadmins
 *   - investors   array   — landlord list for the admin picker
 */
export default function AddTenantToPropertyDialog({
  open, onOpenChange, onCreated, isAdmin = false, investors = [],
}) {
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tenant fields
  const [tenantForm, setTenantForm] = useState({
    full_name: "", email: "", password: "", phone: "", company: "", landlord_id: "",
  });
  // Lease fields
  const [leaseForm, setLeaseForm] = useState({
    property_id: "", unit_label: "", monthly_rent: "", start_date: "", end_date: "", deposit: "",
  });

  // Load properties for the selector every time the dialog opens
  useEffect(() => {
    if (!open) return;
    // Reset both forms on open
    setTenantForm({ full_name: "", email: "", password: "", phone: "", company: "", landlord_id: "" });
    setLeaseForm({ property_id: "", unit_label: "", monthly_rent: "", start_date: "", end_date: "", deposit: "" });
    setLoadingProps(true);
    api
      .get("/properties")
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [open]);

  // Auto-generate a secure temporary password the user can hand to their tenant
  const generatePassword = () => {
    // 10-char alphanumeric — friendly to type, hard to guess
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setTenantForm((f) => ({ ...f, password: out }));
    toast.success("Temporary password generated — share it with the tenant");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isAdmin && !tenantForm.landlord_id) {
      toast.error("Select an investor to assign this tenant to");
      return;
    }
    if (!leaseForm.property_id) {
      toast.error("Select the property this tenant occupies");
      return;
    }
    if (!leaseForm.monthly_rent || Number(leaseForm.monthly_rent) <= 0) {
      toast.error("Monthly rent must be greater than zero");
      return;
    }
    if (!leaseForm.start_date || !leaseForm.end_date) {
      toast.error("Both lease start and end dates are required");
      return;
    }
    if (leaseForm.end_date <= leaseForm.start_date) {
      toast.error("Lease end date must be after start date");
      return;
    }

    setSaving(true);
    let tenantId = null;
    let emailStatus = null;
    try {
      // 1. Create the tenant (auto-sends branded welcome email)
      const payload = { ...tenantForm };
      if (!isAdmin) delete payload.landlord_id;
      const { data: tenant } = await api.post("/tenants", payload);
      tenantId = tenant.id;
      emailStatus = tenant?.welcome_email_status;

      // 2. Create the lease
      await api.post("/leases", {
        property_id: leaseForm.property_id,
        tenant_id: tenantId,
        unit_label: leaseForm.unit_label || "Main",
        monthly_rent: Number(leaseForm.monthly_rent),
        start_date: leaseForm.start_date,
        end_date: leaseForm.end_date,
        deposit: Number(leaseForm.deposit || 0),
      });

      const unitLabel = leaseForm.unit_label || "the property";
      if (tenant?.welcome_email_sent) {
        toast.success(`${tenantForm.full_name} added to ${unitLabel}`, {
          description: `Welcome email sent to ${tenantForm.email}`,
        });
      } else if (emailStatus === "mocked") {
        toast.message(`${tenantForm.full_name} added to ${unitLabel} (MOCK email mode)`, {
          description: `Share manually · ${tenantForm.email} / ${tenantForm.password}`,
        });
      } else {
        toast.warning(`${tenantForm.full_name} added to ${unitLabel} — email send failed`, {
          description: `Share manually · ${tenantForm.email} / ${tenantForm.password}`,
        });
      }
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add tenant + lease");
      // If tenant was created but lease failed, tell the user
      if (tenantId) {
        toast.message(
          "Tenant was created but the lease wasn't — finish adding the lease manually on the Leases page.",
          { duration: 8000 },
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-tenant-to-property-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" /> Add tenant to a property
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            One-step onboarding — creates the tenant AND their lease together.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-2">
          {/* Investor assignment (superadmin only) */}
          {isAdmin && (
            <Section title="Investor assignment">
              <Field label="Assign to investor">
                <select
                  required
                  className="input-clean w-full"
                  data-testid="atp-landlord-select"
                  value={tenantForm.landlord_id}
                  onChange={(e) => setTenantForm({ ...tenantForm, landlord_id: e.target.value })}
                >
                  <option value="">Select investor</option>
                  {investors.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.full_name}{l.company ? ` — ${l.company}` : ""} ({l.email})
                    </option>
                  ))}
                </select>
              </Field>
            </Section>
          )}

          {/* Property selector */}
          <Section title="Property + unit" icon={<Building2 className="w-4 h-4" />}>
            <Field label="Property">
              <select
                required
                className="input-clean w-full"
                data-testid="atp-property-select"
                value={leaseForm.property_id}
                onChange={(e) => setLeaseForm({ ...leaseForm, property_id: e.target.value })}
              >
                <option value="">{loadingProps ? "Loading…" : "Select a property"}</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.address || "no address"}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Unit label">
                <input
                  className="input-clean w-full"
                  data-testid="atp-unit-input"
                  placeholder="Suite 100 / Apt 2B / Main"
                  value={leaseForm.unit_label}
                  onChange={(e) => setLeaseForm({ ...leaseForm, unit_label: e.target.value })}
                />
              </Field>
              <Field label="Monthly rent (USD)">
                <input
                  required
                  type="number"
                  min="1"
                  step="0.01"
                  className="input-clean w-full"
                  data-testid="atp-rent-input"
                  placeholder="2500"
                  value={leaseForm.monthly_rent}
                  onChange={(e) => setLeaseForm({ ...leaseForm, monthly_rent: e.target.value })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Lease start">
                <USDateInput
                  required
                  data-testid="atp-start-input"
                  value={leaseForm.start_date}
                  onChange={(iso) => setLeaseForm({ ...leaseForm, start_date: iso })}
                />
              </Field>
              <Field label="Lease end">
                <USDateInput
                  required
                  data-testid="atp-end-input"
                  value={leaseForm.end_date}
                  onChange={(iso) => setLeaseForm({ ...leaseForm, end_date: iso })}
                />
              </Field>
              <Field label="Deposit (USD)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-clean w-full"
                  data-testid="atp-deposit-input"
                  placeholder="0"
                  value={leaseForm.deposit}
                  onChange={(e) => setLeaseForm({ ...leaseForm, deposit: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          {/* Tenant contact */}
          <Section title="Tenant" icon={<UserPlus className="w-4 h-4" />}>
            <Field label="Full name">
              <input
                required
                className="input-clean w-full"
                data-testid="atp-name-input"
                value={tenantForm.full_name}
                onChange={(e) => setTenantForm({ ...tenantForm, full_name: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <input
                  required
                  type="email"
                  className="input-clean w-full"
                  data-testid="atp-email-input"
                  value={tenantForm.email}
                  onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  inputMode="tel"
                  className="input-clean w-full"
                  data-testid="atp-phone-input"
                  placeholder="(555) 123-4567"
                  value={formatUSPhone(tenantForm.phone)}
                  onChange={(e) =>
                    setTenantForm({ ...tenantForm, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })
                  }
                />
              </Field>
            </div>
            <Field label="Company (optional)">
              <input
                className="input-clean w-full"
                data-testid="atp-company-input"
                value={tenantForm.company}
                onChange={(e) => setTenantForm({ ...tenantForm, company: e.target.value })}
              />
            </Field>
            <Field label="Temporary password">
              <div className="flex gap-2">
                <input
                  required
                  minLength={8}
                  type="text"
                  className="input-clean flex-1"
                  data-testid="atp-pass-input"
                  placeholder="min. 8 characters"
                  value={tenantForm.password}
                  onChange={(e) => setTenantForm({ ...tenantForm, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  data-testid="atp-generate-pass-btn"
                  className="btn-outline-dark flex items-center gap-1.5 text-sm shrink-0"
                  title="Auto-generate a secure 10-character password"
                >
                  <Key className="w-4 h-4" /> Generate
                </button>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Share this password with your tenant once — they'll change it on first login.
              </div>
            </Field>
          </Section>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline-dark"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark disabled:opacity-60"
              data-testid="atp-submit-btn"
              disabled={saving}
            >
              {saving ? "Adding…" : "Add tenant + lease"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="space-y-3">
      <div className="overline text-slate-600 flex items-center gap-2">
        {icon} {title}
      </div>
      <div className="space-y-3 pl-1">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
